/* ============================================================
   MORNING FIX COFFEE
   BUILD #1 — COFFEE PROFILE QUIZ ENGINE
   ============================================================

   RESPONSIBILITY
   ------------------------------------------------------------
   Build #1 is responsible ONLY for:

   • Question-bank management
   • Answer collection
   • Profile scoring
   • Dimension calculation
   • Roast-score calculation
   • Confidence calculation
   • R1–R7 roast classification
   • Adaptive next-question selection
   • Question-bank validation

   EXPLICITLY OUT OF SCOPE
   ------------------------------------------------------------
   • Landing pages
   • Shopify URLs
   • Product IDs
   • Variant IDs
   • Cart operations
   • Subscriptions
   • UI rendering
   • Marketing

   Those belong to later builds.

   ============================================================ */


/* ============================================================
   1. ENGINE VERSION
   ============================================================ */

const BuildMyCoffeeEngine = (function () {

const COFFEE_PROFILE_ENGINE = {

  version: "1.0.0",
  build: 1

};


/* ============================================================
   2. DIMENSION WEIGHTS
   ============================================================ */

const DIMENSION_WEIGHTS = {

  intensity: 0.30,
  roastCharacter: 0.25,
  body: 0.15,
  acidity: 0.15,
  flavor: 0.15

};


/* ============================================================
   3. ROAST RESULTS
   ============================================================

   Seven distinct profile bands.

   IMPORTANT:
   R2, R4 and R6 are intentionally NOT rounded.

   They represent genuine transition profiles.

   ============================================================ */

const ROAST_RESULTS = {

  R1: {
    id: "R1",
    name: "Medium-Light Roast",
    min: 0,
    max: 14,
    landingPage: 1
  },

  R2: {
    id: "R2",
    name: "Medium-Light → Medium",
    min: 15,
    max: 29,
    landingPage: 2
  },

  R3: {
    id: "R3",
    name: "Medium Roast",
    min: 30,
    max: 44,
    landingPage: 3
  },

  R4: {
    id: "R4",
    name: "Medium → Medium-Dark",
    min: 45,
    max: 59,
    landingPage: 4
  },

  R5: {
    id: "R5",
    name: "Medium-Dark Roast",
    min: 60,
    max: 74,
    landingPage: 5
  },

  R6: {
    id: "R6",
    name: "Medium-Dark → Dark",
    min: 75,
    max: 89,
    landingPage: 6
  },

  R7: {
    id: "R7",
    name: "Dark Roast",
    min: 90,
    max: 100,
    landingPage: 7
  }

};


/* ============================================================
   4. QUESTION BANK
   ============================================================

   INSERT THE VALIDATED 24-QUESTION BANK HERE.

   Build #1 expects each question to contain:

   {
     id,
     category,
     questionType,
     priority,
     weight,
     dimensions,
     answers,
     interests,
     libraryTags,
     enabled
   }

   Answer objects should contain:

   {
     id,
     label,
     score
   }

   score must use the dimension names defined above.

   Knowledge questions may exist in the bank but MUST NOT
   contribute roast scoring unless their answers explicitly
   contain scoring data.

   ============================================================ */

const QUESTION_BANK = [

  {
    id: "Q001",
    category: "taste",
    questionType: "preference",
    priority: 1,
    weight: 5,

    question:
      "What kind of coffee sounds most appealing to you?",

    dimensions: [
      "intensity",
      "roastCharacter",
      "body",
      "acidity",
      "flavor"
    ],

    answers: [

      {
        id: "A",
        label: "Bright, lively, and delicate",
        score: {
          intensity: 15,
          roastCharacter: 15,
          body: 20,
          acidity: 85,
          flavor: 85
        }
      },

      {
        id: "B",
        label: "Smooth, balanced, and sweet",
        score: {
          intensity: 40,
          roastCharacter: 40,
          body: 50,
          acidity: 55,
          flavor: 65
        }
      },

      {
        id: "C",
        label: "Rich, full-bodied, and satisfying",
        score: {
          intensity: 70,
          roastCharacter: 70,
          body: 80,
          acidity: 35,
          flavor: 55
        }
      },

      {
        id: "D",
        label: "Deep, bold, and intensely roasted",
        score: {
          intensity: 95,
          roastCharacter: 95,
          body: 90,
          acidity: 15,
          flavor: 35
        }
      }

    ]
  },

  {
    id: "Q002",
    category: "taste",
    questionType: "preference",
    priority: 1,
    weight: 5,

    question:
      "How intense do you like your coffee?",

    dimensions: [
      "intensity"
    ],

    answers: [

      { id: "A", label: "Light and delicate", score: { intensity: 15 } },
      { id: "B", label: "Moderate", score: { intensity: 40 } },
      { id: "C", label: "Rich and pronounced", score: { intensity: 70 } },
      { id: "D", label: "Very bold", score: { intensity: 95 } }

    ]
  },

  {
    id: "Q003",
    category: "taste",
    questionType: "preference",
    priority: 1,
    weight: 4,

    question:
      "Which description sounds best to you?",

    dimensions: [
      "acidity"
    ],

    answers: [

      { id: "A", label: "Bright and lively", score: { acidity: 90 } },
      { id: "B", label: "Crisp but balanced", score: { acidity: 65 } },
      { id: "C", label: "Smooth with little sharpness", score: { acidity: 35 } },
      { id: "D", label: "Very low-acid and mellow", score: { acidity: 10 } }

    ]
  },

  {
    id: "Q004",
    category: "taste",
    questionType: "preference",
    priority: 1,
    weight: 4,

    question:
      "How much body do you like in your coffee?",

    dimensions: [
      "body"
    ],

    answers: [

      { id: "A", label: "Light and tea-like", score: { body: 15 } },
      { id: "B", label: "Smooth and medium-bodied", score: { body: 45 } },
      { id: "C", label: "Full and creamy", score: { body: 75 } },
      { id: "D", label: "Heavy and powerful", score: { body: 95 } }

    ]
  },

  {
    id: "Q005",
    category: "taste",
    questionType: "preference",
    priority: 1,
    weight: 4,

    question:
      "Which flavor profile sounds most appealing?",

    dimensions: [
      "flavor",
      "acidity"
    ],

    answers: [

      { id: "A", label: "Citrus, berries, and floral notes", score: { flavor: 90, acidity: 85 } },
      { id: "B", label: "Fruit, caramel, and sweetness", score: { flavor: 75, acidity: 65 } },
      { id: "C", label: "Chocolate, nuts, and caramel", score: { flavor: 55, acidity: 35 } },
      { id: "D", label: "Dark chocolate, spice, and toasted flavors", score: { flavor: 35, acidity: 15 } }

    ]
  },

  {
    id: "Q006",
    category: "taste",
    questionType: "preference",
    priority: 1,
    weight: 5,

    question:
      "How much roasted flavor do you enjoy?",

    dimensions: [
      "roastCharacter"
    ],

    answers: [

      { id: "A", label: "Very little — I prefer the character of the bean", score: { roastCharacter: 10 } },
      { id: "B", label: "Some — enough to add richness", score: { roastCharacter: 40 } },
      { id: "C", label: "Quite a bit — I enjoy pronounced roast flavor", score: { roastCharacter: 70 } },
      { id: "D", label: "A lot — I love deep, dark roasted flavors", score: { roastCharacter: 95 } }

    ]
  },

  {
    id: "Q007",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 3,

    question:
      "How important is natural sweetness in your coffee?",

    dimensions: [
      "flavor"
    ],

    answers: [

      { id: "A", label: "Extremely important", score: { flavor: 90 } },
      { id: "B", label: "Very important", score: { flavor: 70 } },
      { id: "C", label: "Somewhat important", score: { flavor: 45 } },
      { id: "D", label: "Not very important", score: { flavor: 25 } }

    ]
  },

  {
    id: "Q008",
    category: "taste",
    questionType: "preference",
    priority: 1,
    weight: 4,

    question:
      "How do you feel about bitterness in coffee?",

    dimensions: [
      "roastCharacter"
    ],

    answers: [

      { id: "A", label: "I strongly prefer very little bitterness", score: { roastCharacter: 10 } },
      { id: "B", label: "A little bitterness is fine", score: { roastCharacter: 35 } },
      { id: "C", label: "I enjoy some bitterness", score: { roastCharacter: 65 } },
      { id: "D", label: "I like pronounced bitterness", score: { roastCharacter: 90 } }

    ]
  },

  {
    id: "Q009",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 3,

    question:
      "What kind of finish do you prefer?",

    dimensions: [
      "roastCharacter",
      "body"
    ],

    answers: [

      { id: "A", label: "Clean and quick", score: { roastCharacter: 20, body: 20 } },
      { id: "B", label: "Smooth and lingering", score: { roastCharacter: 45, body: 50 } },
      { id: "C", label: "Rich and lingering", score: { roastCharacter: 70, body: 75 } },
      { id: "D", label: "Long, powerful, roasted finish", score: { roastCharacter: 95, body: 90 } }

    ]
  },

  {
    id: "Q010",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 4,

    question:
      "What do you want your first coffee of the day to feel like?",

    dimensions: [
      "intensity"
    ],

    answers: [

      { id: "A", label: "Light and refreshing", score: { intensity: 15 } },
      { id: "B", label: "Smooth and comforting", score: { intensity: 40 } },
      { id: "C", label: "Rich and satisfying", score: { intensity: 70 } },
      { id: "D", label: "Bold and energizing", score: { intensity: 95 } }

    ]
  },

  {
    id: "Q011",
    category: "taste",
    questionType: "discriminator",
    priority: 1,
    weight: 5,

    question:
      "How do you feel about dark-roasted coffee?",

    dimensions: [
      "intensity",
      "roastCharacter"
    ],

    answers: [

      { id: "A", label: "Usually too intense for me", score: { intensity: 10, roastCharacter: 10 } },
      { id: "B", label: "Sometimes enjoyable", score: { intensity: 40, roastCharacter: 40 } },
      { id: "C", label: "I like it quite a bit", score: { intensity: 70, roastCharacter: 70 } },
      { id: "D", label: "It's one of my favorites", score: { intensity: 95, roastCharacter: 95 } }

    ]
  },

  {
    id: "Q012",
    category: "taste",
    questionType: "discriminator",
    priority: 1,
    weight: 5,

    question:
      "How do you feel about light-roasted coffee?",

    dimensions: [
      "intensity",
      "roastCharacter",
      "acidity"
    ],

    answers: [

      { id: "A", label: "I love bright, expressive coffees", score: { intensity: 10, roastCharacter: 10, acidity: 90 } },
      { id: "B", label: "I enjoy them occasionally", score: { intensity: 35, roastCharacter: 30, acidity: 70 } },
      { id: "C", label: "I generally prefer more roast", score: { intensity: 70, roastCharacter: 70, acidity: 35 } },
      { id: "D", label: "They're usually too light for me", score: { intensity: 95, roastCharacter: 95, acidity: 15 } }

    ]
  },

  {
    id: "Q013",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 3,

    question:
      "How appealing are chocolate flavors in coffee?",

    dimensions: [
      "flavor"
    ],

    answers: [

      { id: "A", label: "Not especially appealing", score: { flavor: 15 } },
      { id: "B", label: "Somewhat appealing", score: { flavor: 40 } },
      { id: "C", label: "Very appealing", score: { flavor: 70 } },
      { id: "D", label: "One of my favorite coffee flavors", score: { flavor: 90 } }

    ]
  },

  {
    id: "Q014",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 3,

    question:
      "How appealing are fruity coffee flavors?",

    dimensions: [
      "flavor",
      "acidity"
    ],

    answers: [

      { id: "A", label: "I prefer little or no fruit character", score: { flavor: 20, acidity: 20 } },
      { id: "B", label: "A little fruit is nice", score: { flavor: 40, acidity: 40 } },
      { id: "C", label: "I enjoy noticeable fruit", score: { flavor: 70, acidity: 70 } },
      { id: "D", label: "I love intensely fruity coffees", score: { flavor: 95, acidity: 90 } }

    ]
  },

  {
    id: "Q015",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 3,

    question:
      "How appealing are nutty and caramel flavors?",

    dimensions: [
      "flavor"
    ],

    answers: [

      { id: "A", label: "Not especially appealing", score: { flavor: 20 } },
      { id: "B", label: "Somewhat appealing", score: { flavor: 45 } },
      { id: "C", label: "Very appealing", score: { flavor: 70 } },
      { id: "D", label: "They're exactly what I look for", score: { flavor: 90 } }

    ]
  },

  {
    id: "Q016",
    category: "taste",
    questionType: "discriminator",
    priority: 2,
    weight: 4,

    question:
      "How do you feel about smoky or toasted flavors?",

    dimensions: [
      "roastCharacter",
      "intensity"
    ],

    answers: [

      { id: "A", label: "I prefer to avoid them", score: { roastCharacter: 10, intensity: 15 } },
      { id: "B", label: "A little is fine", score: { roastCharacter: 35, intensity: 40 } },
      { id: "C", label: "I enjoy them", score: { roastCharacter: 65, intensity: 65 } },
      { id: "D", label: "I love strong roasted flavors", score: { roastCharacter: 95, intensity: 95 } }

    ]
  },

  {
    id: "Q017",
    category: "coffeeStyles",
    questionType: "preference",
    priority: 2,
    weight: 4,

    question:
      "Which espresso sounds most appealing?",

    dimensions: [
      "intensity",
      "roastCharacter",
      "flavor"
    ],

    answers: [

      { id: "A", label: "Bright and complex", score: { intensity: 20, roastCharacter: 15, flavor: 85 } },
      { id: "B", label: "Balanced and sweet", score: { intensity: 40, roastCharacter: 40, flavor: 70 } },
      { id: "C", label: "Rich and chocolatey", score: { intensity: 65, roastCharacter: 65, flavor: 55 } },
      { id: "D", label: "Dark, intense, and powerful", score: { intensity: 95, roastCharacter: 95, flavor: 35 } }

    ]
  },

  {
    id: "Q018",
    category: "brewing",
    questionType: "behavior",
    priority: 3,
    weight: 2,

    question:
      "Which coffee experience sounds most like you?",

    dimensions: [
      "intensity",
      "body"
    ],

    answers: [

      { id: "A", label: "Pour-over / filtered coffee", score: { intensity: 30, body: 30 }, preferences: { brewingMethods: ["pour-over"] } },
      { id: "B", label: "Automatic drip", score: { intensity: 45, body: 45 }, preferences: { brewingMethods: ["drip"] } },
      { id: "C", label: "French press", score: { intensity: 60, body: 75 }, preferences: { brewingMethods: ["french-press"] } },
      { id: "D", label: "Espresso", score: { intensity: 80, body: 80 }, preferences: { brewingMethods: ["espresso"] } }

    ]
  },

  {
    id: "Q019",
    category: "behavior",
    questionType: "behavior",
    priority: 3,
    weight: 2,

    question:
      "How do you usually drink your coffee?",

    dimensions: [
      "body"
    ],

    answers: [

      { id: "A", label: "Black", score: { body: 40 }, preferences: { milkPreferences: ["black"] } },
      { id: "B", label: "Mostly black", score: { body: 45 }, preferences: { milkPreferences: ["mostly-black"] } },
      { id: "C", label: "With a little milk or cream", score: { body: 60 }, preferences: { milkPreferences: ["light-milk"] } },
      { id: "D", label: "With plenty of milk", score: { body: 70 }, preferences: { milkPreferences: ["milk-heavy"] } }

    ]
  },

  {
    id: "Q020",
    category: "behavior",
    questionType: "behavior",
    priority: 3,
    weight: 1,

    question:
      "How do you usually drink your coffee?",

    dimensions: [],

    answers: [

      { id: "A", label: "Black with no sweetener", preferences: { coffeeStyles: ["black", "unsweetened"] } },
      { id: "B", label: "Black with sweetener", preferences: { coffeeStyles: ["black", "sweetened"] } },
      { id: "C", label: "Milk/cream with no sweetener", preferences: { coffeeStyles: ["milk", "unsweetened"] } },
      { id: "D", label: "Milk/cream and sweetener", preferences: { coffeeStyles: ["milk", "sweetened"] } }

    ]
  },

  {
    id: "Q021",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 4,

    question:
      "Which mouthfeel sounds best?",

    dimensions: [
      "body"
    ],

    answers: [

      { id: "A", label: "Light and crisp", score: { body: 15 } },
      { id: "B", label: "Silky and smooth", score: { body: 45 } },
      { id: "C", label: "Rich and creamy", score: { body: 75 } },
      { id: "D", label: "Heavy and intense", score: { body: 95 } }

    ]
  },

  {
    id: "Q022",
    category: "taste",
    questionType: "preference",
    priority: 2,
    weight: 4,

    question:
      "What do you value most in a coffee?",

    dimensions: [
      "intensity",
      "roastCharacter",
      "acidity",
      "flavor"
    ],

    answers: [

      { id: "A", label: "Brightness and distinctive character", score: { intensity: 20, roastCharacter: 15, acidity: 90, flavor: 90 } },
      { id: "B", label: "Balance and sweetness", score: { intensity: 40, roastCharacter: 40, acidity: 60, flavor: 75 } },
      { id: "C", label: "Richness and comfort", score: { intensity: 70, roastCharacter: 70, acidity: 35, flavor: 60 } },
      { id: "D", label: "Power and intensity", score: { intensity: 95, roastCharacter: 95, acidity: 15, flavor: 35 } }

    ]
  },

  {
    id: "Q023",
    category: "behavior",
    questionType: "behavior",
    priority: 3,
    weight: 2,

    question:
      "Which sounds more like you?",

    dimensions: [
      "intensity",
      "flavor"
    ],

    answers: [

      { id: "A", label: "I love discovering unusual coffees", score: { intensity: 25, flavor: 85 } },
      { id: "B", label: "I enjoy exploring but want balance", score: { intensity: 45, flavor: 70 } },
      { id: "C", label: "I usually know what I like", score: { intensity: 65, flavor: 50 } },
      { id: "D", label: "Give me the bold coffee I already love", score: { intensity: 90, flavor: 35 } }

    ]
  },

  {
    id: "Q024",
    category: "taste",
    questionType: "discriminator",
    priority: 1,
    weight: 5,

    question:
      "If you had to choose one coffee right now, which would you reach for?",

    dimensions: [
      "intensity",
      "roastCharacter"
    ],

    answers: [

      { id: "A", label: "Medium-Light Roast", score: { intensity: 10, roastCharacter: 10 } },
      { id: "B", label: "Medium Roast", score: { intensity: 40, roastCharacter: 40 } },
      { id: "C", label: "Medium-Dark Roast", score: { intensity: 70, roastCharacter: 70 } },
      { id: "D", label: "Dark Roast", score: { intensity: 95, roastCharacter: 95 } }

    ]
  }

];


/* ============================================================
   5. UTILITY
   ============================================================ */

function clamp(value, min, max) {

  return Math.min(
    Math.max(value, min),
    max
  );

}


/* ============================================================
   6. CREATE EMPTY DIMENSIONS
   ============================================================ */

function createEmptyDimensions() {

  return {

    intensity: 0,
    roastCharacter: 0,
    body: 0,
    acidity: 0,
    flavor: 0

  };

}


/* ============================================================
   7. CREATE PROFILE
   ============================================================ */

function createProfile() {

  return {

    version: COFFEE_PROFILE_ENGINE.version,

    answeredQuestions: [],

    dimensions: createEmptyDimensions(),

    roastScore: 0,

    roastResult: null,

    confidence: 0,

    complete: false,

    _answers: []

  };

}


/* ============================================================
   8. FIND QUESTION
   ============================================================ */

function findQuestion(questionId) {

  return QUESTION_BANK.find(function(question) {

    return question.id === questionId;

  }) || null;

}


/* ============================================================
   9. FIND ANSWER
   ============================================================ */

function findAnswer(question, answerId) {

  if (!question || !Array.isArray(question.answers)) {

    return null;

  }

  return question.answers.find(function(answer) {

    return answer.id === answerId;

  }) || null;

}


/* ============================================================
   10. CALCULATE DIMENSIONS
   ============================================================

   IMPORTANT:
   This function is intentionally deterministic.

   It calculates dimensions ONLY from profile._answers.

   It does not perform manual accumulation.

   ============================================================ */

function calculateDimensions(profile) {

  if (!profile || !Array.isArray(profile._answers)) {

    throw new Error(
      "calculateDimensions requires a valid profile with _answers."
    );

  }

  const totals = createEmptyDimensions();

  const counts = createEmptyDimensions();

  profile._answers.forEach(function(record) {

    if (!record || !record.score) {

      return;

    }

    Object.keys(DIMENSION_WEIGHTS).forEach(function(dimension) {

      if (
        typeof record.score[dimension] === "number"
      ) {

        totals[dimension] +=
          clamp(record.score[dimension], 0, 100);

        counts[dimension] += 1;

      }

    });

  });


  Object.keys(DIMENSION_WEIGHTS).forEach(function(dimension) {

    if (counts[dimension] > 0) {

      totals[dimension] =
        totals[dimension] /
        counts[dimension];

    } else {

      totals[dimension] = 0;

    }

  });


  return totals;

}


/* ============================================================
   11. CALCULATE ROAST SCORE
   ============================================================ */

function calculateRoastScore(dimensions) {

  if (!dimensions) {

    throw new Error(
      "calculateRoastScore requires dimensions."
    );

  }

  let score = 0;

  Object.keys(DIMENSION_WEIGHTS).forEach(function(dimension) {

    const value =
      typeof dimensions[dimension] === "number"
        ? clamp(dimensions[dimension], 0, 100)
        : 0;

    score +=
      value * DIMENSION_WEIGHTS[dimension];

  });

  return clamp(
    Math.round(score * 100) / 100,
    0,
    100
  );

}


/* ============================================================
   12. DETERMINE ROAST RESULT
   ============================================================ */


function determineRoastResult(roastScore) {
  const score = clamp(roastScore, 0, 100);
  const bandScore = Math.floor(score);

  const result = Object.values(ROAST_RESULTS).find(function(roast) {
    return bandScore >= roast.min && bandScore <= roast.max;
  });

  if (!result) {
    throw new Error("Unable to determine roast result for score: " + score);
  }

  return result;
}


/* ============================================================
   13. COUNT MEANINGFUL ANSWERS
   ============================================================ */

function getMeaningfulAnswerCount(profile) {

  if (!profile || !Array.isArray(profile._answers)) {

    return 0;

  }

  return profile._answers.filter(function(record) {

    if (!record || !record.score) {

      return false;

    }

    return Object.keys(DIMENSION_WEIGHTS).some(function(dimension) {

      return (
        typeof record.score[dimension] === "number"
      );

    });

  }).length;

}


/* ============================================================
   14. DIMENSION EVIDENCE
   ============================================================ */

function getDimensionEvidence(profile) {

  const evidence = {};

  Object.keys(DIMENSION_WEIGHTS).forEach(function(dimension) {

    evidence[dimension] = false;

  });


  if (
    !profile ||
    !Array.isArray(profile._answers)
  ) {

    return evidence;

  }


  profile._answers.forEach(function(record) {

    if (!record || !record.score) {

      return;

    }

    Object.keys(DIMENSION_WEIGHTS).forEach(function(dimension) {

      if (
        typeof record.score[dimension] === "number"
      ) {

        evidence[dimension] = true;

      }

    });

  });


  return evidence;

}


/* ============================================================
   15. CONTRADICTION CHECK
   ============================================================

   Build #1 reserves the contradiction concept here.

   The engine does not manufacture contradictions from missing
   data. A contradiction must be explicitly represented by the
   question bank / answer data.

   ============================================================ */

function hasMajorUnresolvedContradiction(profile) {

  if (
    !profile ||
    !Array.isArray(profile._answers)
  ) {

    return false;

  }

  return profile._answers.some(function(record) {

    return (
      record &&
      record.majorContradiction === true &&
      record.contradictionResolved !== true
    );

  });

}


/* ============================================================
   16. CALCULATE CONFIDENCE
   ============================================================

   Confidence requires:

   • At least 15 meaningful answers
   • All five dimensions evidenced
   • No major unresolved contradiction
   • Maximum confidence capped at 85 until the above conditions
     are satisfied

   ============================================================ */

function calculateConfidence(profile) {

  if (!profile) {

    return 0;

  }

  const meaningfulAnswers =
    getMeaningfulAnswerCount(profile);

  const evidence =
    getDimensionEvidence(profile);

  const allDimensionsEvidenced =
    Object.keys(DIMENSION_WEIGHTS).every(function(dimension) {

      return evidence[dimension] === true;

    });

  const contradiction =
    hasMajorUnresolvedContradiction(profile);


  /*
     Answer coverage.

     15 meaningful answers = full answer coverage.
  */

  const answerCoverage =
    clamp(
      meaningfulAnswers / 10,
      0,
      1
    );


  /*
     Dimension coverage.
  */

  const evidencedCount =
    Object.keys(DIMENSION_WEIGHTS).filter(function(dimension) {

      return evidence[dimension];

    }).length;

  const dimensionCoverage =
    evidencedCount /
    Object.keys(DIMENSION_WEIGHTS).length;


  /*
     Base confidence.

     Answers and dimensional evidence both matter.
  */

  let confidence =
    (
      answerCoverage * 0.70
    ) +
    (
      dimensionCoverage * 0.30
    );


  /*
     Major unresolved contradiction reduces confidence.
  */

  if (contradiction) {

    confidence *= 0.50;

  }


  /*
     Confidence cannot exceed 85% until all requirements
     are satisfied.
  */

  const requirementsSatisfied =
    meaningfulAnswers >= 10 &&
    allDimensionsEvidenced &&
    !contradiction;


  if (!requirementsSatisfied) {

    confidence =
      Math.min(confidence, 0.85);

  }


  return Math.round(
    clamp(confidence, 0, 1) * 100
  );

}


/* ============================================================
   17. CHECK PROFILE COMPLETION
   ============================================================ */

function isProfileComplete(profile) {

  if (!profile) {

    return false;

  }

  const meaningfulAnswers =
    getMeaningfulAnswerCount(profile);

  const evidence =
    getDimensionEvidence(profile);

  const allDimensionsEvidenced =
    Object.keys(DIMENSION_WEIGHTS).every(function(dimension) {

      return evidence[dimension] === true;

    });

  const contradiction =
    hasMajorUnresolvedContradiction(profile);


  return (

    meaningfulAnswers >= 10 &&
    allDimensionsEvidenced &&
    !contradiction

  );

}


/* ============================================================
   18. RECALCULATE PROFILE
   ============================================================ */

function recalculateProfile(profile) {

  if (!profile) {

    throw new Error(
      "A profile is required."
    );

  }


  profile.dimensions =
    calculateDimensions(profile);


  profile.roastScore =
    calculateRoastScore(
      profile.dimensions
    );


  profile.roastResult =
    determineRoastResult(
      profile.roastScore
    );


  profile.confidence =
    calculateConfidence(profile);


  profile.complete =
    isProfileComplete(profile);


  return profile;

}


/* ============================================================
   19. APPLY ANSWER
   ============================================================

   Internal function.

   No manual dimension accumulation.

   ============================================================ */

function applyAnswer(
  profile,
  question,
  answer
) {

  if (!profile) {

    throw new Error(
      "Profile is required."
    );

  }

  if (!question) {

    throw new Error(
      "Question is required."
    );

  }

  if (!answer) {

    throw new Error(
      "Answer is required."
    );

  }


  if (
    profile.answeredQuestions.includes(
      question.id
    )
  ) {

    throw new Error(
      "Question " +
      question.id +
      " has already been answered."
    );

  }


  profile.answeredQuestions.push(
    question.id
  );


  profile._answers.push({

    questionId: question.id,

    answerId: answer.id,

    score: answer.score || {},

    majorContradiction:
      answer.majorContradiction === true,

    contradictionResolved:
      answer.contradictionResolved === true

  });


  recalculateProfile(profile);


  return profile;

}


/* ============================================================
   20. ANSWER PUBLIC WRAPPER
   ============================================================ */

function answer(
  profile,
  questionId,
  answerId
) {

  if (!profile) {

    throw new Error(
      "Profile is required."
    );

  }


  const question =
    findQuestion(questionId);


  if (!question) {

    throw new Error(
      "Question not found: " +
      questionId
    );

  }


  const selectedAnswer =
    findAnswer(
      question,
      answerId
    );


  if (!selectedAnswer) {

    throw new Error(
      "Answer not found: " +
      answerId +
      " for question " +
      questionId
    );

  }


  return applyAnswer(
    profile,
    question,
    selectedAnswer
  );

}


/* ============================================================
   21. NEXT QUESTION
   ============================================================

   Adaptive selection:

   • Enabled questions only
   • Never repeat answered questions
   • Higher priority first
   • Higher unresolved dimensional value first

   ============================================================ */

function getNextQuestion(profile) {

  if (!profile) {

    throw new Error(
      "Profile is required."
    );

  }


  const answered =
    new Set(
      profile.answeredQuestions
    );


  const available =
    QUESTION_BANK.filter(function(question) {

      return (
        question.enabled !== false &&
        !answered.has(question.id)
      );

    });


  if (available.length === 0) {

    return null;

  }


  const evidence =
    getDimensionEvidence(profile);


  available.sort(function(a, b) {

    const aDimensions =
      Array.isArray(a.dimensions)
        ? a.dimensions
        : [];

    const bDimensions =
      Array.isArray(b.dimensions)
        ? b.dimensions
        : [];


    const aUnresolved =
      aDimensions.filter(function(dimension) {

        return !evidence[dimension];

      }).length;


    const bUnresolved =
      bDimensions.filter(function(dimension) {

        return !evidence[dimension];

      }).length;


    if (
      bUnresolved !==
      aUnresolved
    ) {

      return (
        bUnresolved -
        aUnresolved
      );

    }


    const priorityA =
      typeof a.priority === "number"
        ? a.priority
        : 0;

    const priorityB =
      typeof b.priority === "number"
        ? b.priority
        : 0;


    if (
      priorityB !==
      priorityA
    ) {

      return (
        priorityB -
        priorityA
      );

    }


    const weightA =
      typeof a.weight === "number"
        ? a.weight
        : 0;

    const weightB =
      typeof b.weight === "number"
        ? b.weight
        : 0;


    return (
      weightB -
      weightA
    );

  });


  return available[0];

}


/* ============================================================
   22. QUESTION-BANK VALIDATION
   ============================================================ */

function validateQuestionBank(
  questions = QUESTION_BANK
) {

  if (!Array.isArray(questions)) {

    throw new Error(
      "Question bank must be an array."
    );

  }


  if (questions.length === 0) {

    throw new Error(
      "Question bank is empty. The engine cannot function " +
      "with zero questions defined."
    );

  }


  const ids =
    new Set();


  questions.forEach(function(question, index) {

    if (!question.id) {

      throw new Error(
        "Question at index " +
        index +
        " is missing an id."
      );

    }


    if (ids.has(question.id)) {

      throw new Error(
        "Duplicate question ID: " +
        question.id
      );

    }


    ids.add(question.id);


    if (
      !Array.isArray(
        question.answers
      )
    ) {

      throw new Error(
        "Question " +
        question.id +
        " must contain answers."
      );

    }


    const answerIds =
      new Set();


    question.answers.forEach(function(answer) {

      if (!answer.id) {

        throw new Error(
          "Question " +
          question.id +
          " contains an answer without an ID."
        );

      }


      if (
        answerIds.has(answer.id)
      ) {

        throw new Error(
          "Duplicate answer ID " +
          answer.id +
          " in question " +
          question.id
        );

      }


      answerIds.add(answer.id);


      if (
        answer.score
      ) {

        Object.keys(
          answer.score
        ).forEach(function(dimension) {

          if (
            !Object.prototype.hasOwnProperty.call(
              DIMENSION_WEIGHTS,
              dimension
            )
          ) {

            throw new Error(
              "Invalid scoring dimension '" +
              dimension +
              "' in question " +
              question.id +
              ", answer " +
              answer.id
            );

          }


          const value =
            answer.score[dimension];


          if (
            typeof value !== "number" ||
            value < 0 ||
            value > 100
          ) {

            throw new Error(
              "Invalid score for " +
              dimension +
              " in question " +
              question.id +
              ", answer " +
              answer.id
            );

          }

        });

      }

    });

  });


  /*
     Verify dimension weights.
  */

  const totalWeight =
    Object.values(
      DIMENSION_WEIGHTS
    ).reduce(function(sum, value) {

      return sum + value;

    }, 0);


  if (
    Math.abs(
      totalWeight - 1
    ) > 0.000001
  ) {

    throw new Error(
      "Dimension weights must sum to exactly 1.0."
    );

  }


  /*
     Verify roast-result boundaries.
  */

  const results =
    Object.values(
      ROAST_RESULTS
    ).sort(function(a, b) {

      return a.min - b.min;

    });


  results.forEach(function(result, index) {

    if (
      result.min < 0 ||
      result.max > 100 ||
      result.min > result.max
    ) {

      throw new Error(
        "Invalid roast boundary: " +
        result.id
      );

    }


    if (index > 0) {

      const previous =
        results[index - 1];


      if (
        result.min !==
        previous.max + 1
      ) {

        throw new Error(
          "Roast-result boundary gap or overlap between " +
          previous.id +
          " and " +
          result.id
        );

      }

    }

  });


  return {

    valid: true,

    questionCount:
      questions.length,

    dimensionWeights:
      Object.assign(
        {},
        DIMENSION_WEIGHTS
      ),

    roastResults:
      Object.keys(
        ROAST_RESULTS
      ).length

  };

}


/* ============================================================
   23. PUBLIC API
   ============================================================

   calculateDimensions is intentionally NOT exported.

   It is an internal calculation function.

   ============================================================ */

const CoffeeProfile = {

  version:
    COFFEE_PROFILE_ENGINE.version,

  build:
    COFFEE_PROFILE_ENGINE.build,

  createProfile,

  answer,

  getNextQuestion,

  calculateRoastScore,

  determineRoastResult,

  calculateConfidence,

  isProfileComplete,

  validateQuestionBank,

  getDimensionEvidence,

  getMeaningfulAnswerCount,

  dimensions:
    Object.assign(
      {},
      DIMENSION_WEIGHTS
    ),

  roastResults:
    Object.assign(
      {},
      ROAST_RESULTS
    )

};


/* ============================================================
   24. INITIAL VALIDATION
   ============================================================

   Fail immediately if the question bank is malformed.

   ============================================================ */

validateQuestionBank();


/* ============================================================
   25. OPTIONAL GLOBAL EXPORT
   ============================================================

   Browser-safe exposure.

   ============================================================ */


  return CoffeeProfile;

})();

if (
  typeof window !== "undefined"
) {

  window.CoffeeProfile =
    BuildMyCoffeeEngine;

}


/* ============================================================
   END BUILD #1
   ============================================================ */